<template>
  <div class="kazang-vcd-selector">
    <div class="vcd-card">
      <h3 class="card-title">Select Payment Account</h3>
      <p class="card-subtitle">Choose a Kazang vendor account to pay from</p>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="skeleton-item" v-for="i in 3" :key="i">
          <div class="skeleton-icon"></div>
          <div class="skeleton-content">
            <div class="skeleton-line"></div>
            <div class="skeleton-line short"></div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="accounts.length === 0" class="empty-state">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <p>No Kazang accounts found for this customer.</p>
      </div>

      <!-- Account List -->
      <div v-else class="accounts-list">
        <button
          v-for="account in accounts"
          :key="account.kazangID"
          type="button"
          class="account-item"
          :class="{ 'is-processing': isProcessing && selectedAccount === account }"
          :disabled="isProcessing"
          @click="selectAccount(account)"
        >
          <div class="account-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="2" y="3" width="20" height="14" rx="2"></rect>
              <path d="M2 10h20"></path>
            </svg>
          </div>
          <div class="account-content">
            <span class="account-name">{{ account.storeName }}</span>
            <span class="account-id">ID: {{ account.kazangID }}</span>
          </div>
          <div class="account-action">
            <div v-if="isProcessing && selectedAccount === account" class="spinner"></div>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KazangVcdSelector',
  props: {
    accounts: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isProcessing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedAccount: null
    }
  },
  methods: {
    selectAccount(account) {
      if (!this.isProcessing) {
        this.selectedAccount = account
        this.$emit('select-account', account)
      }
    }
  }
}
</script>

<style scoped>
.kazang-vcd-selector {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.vcd-card {
  background: #ffffff;
  border: 1px solid #e4e4e7;
  border-radius: 0.75rem;
  padding: 1.5rem;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  color: #18181b;
  margin: 0 0 0.25rem 0;
}

.card-subtitle {
  font-size: 0.875rem;
  color: #71717a;
  margin: 0 0 1.25rem 0;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.skeleton-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #fafafa;
  border-radius: 0.5rem;
}

.skeleton-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(90deg, #e4e4e7 25%, #f4f4f5 50%, #e4e4e7 75%);
  background-size: 200% 100%;
  border-radius: 0.5rem;
  animation: shimmer 1.5s infinite;
}

.skeleton-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.skeleton-line {
  height: 12px;
  background: linear-gradient(90deg, #e4e4e7 25%, #f4f4f5 50%, #e4e4e7 75%);
  background-size: 200% 100%;
  border-radius: 0.25rem;
  animation: shimmer 1.5s infinite;
}

.skeleton-line.short {
  width: 60%;
  height: 10px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 2rem 1rem;
  color: #71717a;
}

.empty-state svg {
  width: 48px;
  height: 48px;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  font-size: 0.9375rem;
}

/* Account List */
.accounts-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.account-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 1rem;
  background: #fafafa;
  border: 1px solid #e4e4e7;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.account-item:hover:not(:disabled) {
  background: #f4f4f5;
  border-color: #a1a1aa;
}

.account-item:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.account-item.is-processing {
  background: #f0f9ff;
  border-color: #0ea5e9;
}

.account-icon {
  width: 40px;
  height: 40px;
  background: #ffffff;
  border: 1px solid #e4e4e7;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #71717a;
}

.account-icon svg {
  width: 20px;
  height: 20px;
}

.account-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.account-name {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #18181b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-id {
  font-size: 0.75rem;
  color: #71717a;
}

.account-action {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: #a1a1aa;
}

.account-action svg {
  width: 100%;
  height: 100%;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e4e4e7;
  border-top-color: #0ea5e9;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
